/**
 * Card-entry validation for the sign-up "card on file" form — the same rules the
 * Customer Portal's Add card and the CRM's Payment methods panel apply, so a card
 * the storefront accepts is a card both of them accept.
 *
 * **The number never leaves the browser.** What the form DOES with the PAN is
 * derive the facts billing stores — `brand`, `last4` and the expiry — and drop
 * the rest. `/bff/deposit/card-on-file` has no card-number field (its schema is
 * `.strict()`, so one is refused, not dropped), and neither does billing's
 * `POST /v1/us/payment-methods`, which is where the card lands once the account
 * exists. No CVV either: it cannot be checked against anything locally, so a
 * field for one is PCI-DSS scope bought for no answer.
 *
 * ---------------------------------------------------------------------------
 * THIS IS THE THIRD COPY, AND THAT IS A KNOWN DEBT
 * ---------------------------------------------------------------------------
 *
 * The body below is taken verbatim from
 * `US/src/module/payment/services/cardEntry.ts`, which is itself a copy of
 * `Customer_Portal/src/lib/cardEntry.ts`. Three copies of PCI-adjacent
 * validation: an IIN range added to one and not the others means the storefront
 * saves a card the CRM then calls invalid. The right home is a shared
 * `@fluxora/*` package in Global_Common_Frontend_Packages. Until it moves there,
 * if you edit any of the three, edit all of them.
 *
 * Everything below is offline arithmetic: Luhn, IIN ranges, lengths, a calendar
 * comparison. It catches typos and mis-keyed cards; it cannot tell you an account
 * exists, has funds, or has not been reported stolen — only an authorisation
 * does that.
 */

/** The four networks the portal draws a mark for and the BFF stores a name for. */
export type CardBrand = 'Visa' | 'Mastercard' | 'American Express' | 'Discover';

interface BrandSpec {
  readonly brand: CardBrand;
  /** Issuer Identification Number prefixes, anchored. */
  readonly iin: RegExp;
  /** Every PAN length the network actually issues at. */
  readonly lengths: readonly number[];
  /** Digit grouping as the number is printed on the plastic. */
  readonly groups: readonly number[];
}

/**
 * Tested in order, first hit wins. Each `iin` is written to be mutually
 * exclusive anyway, so a range added carelessly later would be shadowed
 * visibly rather than change another network's answer.
 */
const SPECS: readonly BrandSpec[] = [
  { brand: 'Visa', iin: /^4/, lengths: [13, 16, 19], groups: [4, 4, 4, 4] },
  {
    brand: 'Mastercard',
    // 51–55, plus the 2221–2720 range Mastercard opened in 2017. Written as
    // alternatives because "2221 to 2720" is not a prefix — 2221 is in and 2220
    // is not, and a lazy /^2[2-7]/ takes both.
    iin: /^(5[1-5]|222[1-9]|22[3-9]\d|2[3-6]\d\d|27[01]\d|2720)/,
    lengths: [16],
    groups: [4, 4, 4, 4],
  },
  // Amex prints 15 digits as 4-6-5, and someone checking their typing against
  // the card in their hand is comparing shapes before digits.
  { brand: 'American Express', iin: /^3[47]/, lengths: [15], groups: [4, 6, 5] },
  {
    brand: 'Discover',
    iin: /^(6011|65|64[4-9]|622(12[6-9]|1[3-9]\d|[2-8]\d\d|9[01]\d|92[0-5]))/,
    lengths: [16, 19],
    groups: [4, 4, 4, 4],
  },
];

/** Longest PAN any network issues — the cap while no brand is known yet. */
const MAX_PAN = 19;

/** Strips everything that is not a digit: spaces, dashes, a pasted bullet. */
export function cardDigits(raw: string): string {
  return raw.replace(/\D/g, '');
}

function specFor(digits: string): BrandSpec | null {
  if (!digits) return null;
  return SPECS.find((s) => s.iin.test(digits)) ?? null;
}

/**
 * The network a number belongs to, from as few as one digit — this is what
 * puts the Visa mark inside the field while the number is still being typed.
 * `null` while nothing matches yet, the empty string included.
 */
export function detectBrand(raw: string): CardBrand | null {
  return specFor(cardDigits(raw))?.brand ?? null;
}

/** How many digits the field should accept, once the network is known. */
export function maxDigits(raw: string): number {
  const spec = specFor(cardDigits(raw));
  return spec ? Math.max(...spec.lengths) : MAX_PAN;
}

/**
 * Groups the digits the way the network prints them, so the field reads as a
 * card rather than as a 16-digit integer. Truncates at the network's longest
 * length — typing past the end of a Visa is a slip, not an intention.
 */
export function formatCardNumber(raw: string): string {
  const spec = specFor(cardDigits(raw));
  const digits = cardDigits(raw).slice(0, spec ? Math.max(...spec.lengths) : MAX_PAN);
  const groups = spec?.groups ?? [4, 4, 4, 4];
  const out: string[] = [];
  let i = 0;
  for (const size of groups) {
    if (i >= digits.length) break;
    out.push(digits.slice(i, i + size));
    i += size;
  }
  // Whatever the group list did not cover — the tail of a 19-digit Visa — keeps
  // running in fours rather than arriving as one long block.
  while (i < digits.length) {
    out.push(digits.slice(i, i + 4));
    i += 4;
  }
  return out.join(' ');
}

/**
 * The Luhn check digit, which every issued PAN satisfies. It is what catches a
 * transposed pair or one mistyped digit — the overwhelming majority of what
 * actually goes wrong in this field.
 */
export function luhnValid(digits: string): boolean {
  if (digits.length < 2) return false;
  let sum = 0;
  let double = false;
  // Right to left: the check digit is the last one, and the doubling alternates
  // away from it.
  for (const ch of [...digits].reverse()) {
    let n = Number(ch);
    if (double) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    double = !double;
  }
  return sum % 10 === 0;
}

export type CardNumberCheck =
  | { readonly ok: true; readonly brand: CardBrand; readonly last4: string }
  | { readonly ok: false; readonly error: string };

/**
 * Every rejection names the thing that is wrong, in the order someone would
 * discover it: nothing typed, wrong network, wrong length, wrong digits. One
 * blanket "invalid card number" for all four sends a customer re-reading digits
 * that were never the problem.
 */
export function checkCardNumber(raw: string): CardNumberCheck {
  const digits = cardDigits(raw);
  if (!digits) return { ok: false, error: 'Enter the number printed on your card.' };

  const spec = specFor(digits);
  if (!spec) {
    return { ok: false, error: 'We accept Visa, Mastercard, American Express and Discover.' };
  }
  if (!spec.lengths.includes(digits.length)) {
    const shortest = Math.min(...spec.lengths);
    const lengths = spec.lengths.join(' or ');
    return {
      ok: false,
      error:
        digits.length < shortest
          ? `A ${spec.brand} number is ${lengths} digits — this one is short.`
          : `A ${spec.brand} number is ${lengths} digits.`,
    };
  }
  // Luhn last: it is the only message that cannot say WHERE the mistake is, so
  // it should only be reached once the shape is otherwise right.
  if (!luhnValid(digits)) {
    return { ok: false, error: 'That card number is not valid — check the digits.' };
  }
  return { ok: true, brand: spec.brand, last4: digits.slice(-4) };
}

/**
 * MM/YY as it is typed. A leading digit above 1 can only be a month on its own
 * ("5" is May, not the start of 5x), so it pads itself; the slash appears only
 * once there is a year digit to sit after it, which is what keeps backspace
 * from getting stuck on a separator the formatter re-adds every keystroke.
 */
export function formatExpiry(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (d.length === 1 && Number(d) > 1) d = `0${d}`;
  d = d.slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
}

export type ExpiryCheck = { readonly ok: true } | { readonly ok: false; readonly error: string };

/** Two-digit years are this century — a card does not expire in 1928. */
const CENTURY = 2000;
/** Issuers do not date cards further out than this; beyond it is a typo. */
const MAX_YEARS_AHEAD = 20;

/**
 * `now` is a parameter rather than a `new Date()` inside, because "has this
 * expired" is a question about a moment and a test has to be able to name
 * which one. A card is good through the LAST day of its printed month, so the
 * comparison is month-to-month and never day-to-day.
 */
export function checkExpiry(value: string, now: Date = new Date()): ExpiryCheck {
  const m = /^(\d{2})\/(\d{2})$/.exec(value.trim());
  if (!m) return { ok: false, error: 'Use MM/YY.' };

  const month = Number(m[1]);
  const year = CENTURY + Number(m[2]);
  if (month < 1 || month > 12) return { ok: false, error: 'Enter a month between 01 and 12.' };

  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;
  if (year < nowYear || (year === nowYear && month < nowMonth)) {
    return { ok: false, error: 'That card has expired.' };
  }
  if (year > nowYear + MAX_YEARS_AHEAD) return { ok: false, error: 'Check the expiry year.' };
  return { ok: true };
}

/**
 * MM/YY split into the two numbers billing stores.
 *
 * The CRM needs this and the portal does not: the portal keeps the expiry as the
 * typed string in its own ledger, while `billing.payment_method` has
 * `expiry_month` and `expiry_year` columns with a both-or-neither constraint.
 *
 * Returns null for anything `checkExpiry` would reject, so a caller cannot send
 * half an expiry and get a 400 back from a field the agent cannot see.
 */
export function expiryParts(value: string): { month: number; year: number } | null {
  const m = /^(\d{2})\/(\d{2})$/.exec(value.trim());
  if (m === null) return null;
  const month = Number(m[1]);
  const year = CENTURY + Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { month, year };
}
